// Terrain model: the patient's readings, judged against the catalog's guideline bands, rolled up per body system.
// Pure. Nothing here invents a value: a marker with no reading is "not on file", a band that needs the ESC/EAS risk
// category (not on the record) is not applied, and a threshold (Lp(a), HbA1c, hsCRP) is shown but never counted.

import {
  TERRAIN_MARKERS,
  TERRAIN_SYSTEMS,
  bandStatus,
  bpBandStatus,
  markersForSystem,
  type TerrainBand,
  type TerrainMarker,
  type TerrainSystem,
  type TerrainSystemId,
} from './terrain-catalog';

/** One dated reading. Blood pressure carries its diastolic in `value2`. */
export interface TerrainReading {
  value: number;
  value2?: number;
  unit: string;
  /** ISO date-time. */
  date: string;
  refLow?: number | null;
  refHigh?: number | null;
  /** Set when the value was converted to the marker's unit: what the lab reported. */
  reported?: { value: number; unit: string };
}

/** Readings per marker key, oldest first or in any order (sorted here). */
export type TerrainReadings = Partial<Record<string, TerrainReading[]>>;

export type MarkerStatus = 'not-on-file' | 'no-band' | 'within' | 'outside';

export interface MarkerRow {
  marker: TerrainMarker;
  latest: TerrainReading | null;
  previous: TerrainReading | null;
  /** The band applied, if any. */
  band: TerrainBand | null;
  /** Why no band applies, when the marker has bands but none was applied. */
  bandNote: string | null;
  status: MarkerStatus;
  /** Latest vs its band: the arrow. Null when no band applies. */
  direction: 'above' | 'below' | 'within' | null;
  /** Latest vs the lab's reference range. Null when the lab sent none. */
  labRange: 'above' | 'below' | 'within' | null;
  /** True when the band is counted in "x of y in target". */
  counted: boolean;
  /** Previous reading's status against the same band; null when there is none. */
  previousWithin: boolean | null;
}

export interface SystemSummary {
  system: TerrainSystem;
  rows: MarkerRow[];
  /** Counted markers with a latest reading. */
  banded: number;
  inTarget: number;
  /** Counted markers with a previous reading. */
  previousBanded: number;
  previousInTarget: number;
  /** Markers with a latest reading judged against ANY band, target or threshold: what the radar draws. */
  assessed: number;
  /** Of those, within the band (in target, or under / over the threshold on the safe side). */
  assessedWithin: number;
  previousAssessed: number;
  previousAssessedWithin: number;
  state: 'attention' | 'within' | 'not-derived';
}

export const RISK_CATEGORY_NOTE = 'Target not set · risk category not established';
export const NO_TARGET_NOTE = 'No guideline target named';

function byDate(a: TerrainReading, b: TerrainReading): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
}

function against(r: TerrainReading, b: TerrainBand): 'above' | 'below' | 'within' {
  if (b.diastolicHigh != null && r.value2 != null) return bpBandStatus(r.value, r.value2, b);
  return bandStatus(r.value, b);
}

function labRange(r: TerrainReading): 'above' | 'below' | 'within' | null {
  if (r.refLow == null && r.refHigh == null) return null;
  if (r.refLow != null && r.value < r.refLow) return 'below';
  if (r.refHigh != null && r.value > r.refHigh) return 'above';
  return 'within';
}

/** Units compared loosely: case, spaces, ² and ³ do not matter ("kg/m2" is "kg/m²"), as seen on dev data. */
const unitKey = (u: string) => u.toLowerCase().replace(/\s+/g, '').replace(/²/g, '2').replace(/³/g, '3');

/** Conventional → SI factors per marker (value in `from` × factor = value in the marker's unit). Standard clinical
 *  conversions (cholesterol 1 mmol/L = 38.67 mg/dL, TG 88.57, glucose 18.02, UACR 1 mg/mmol = 8.84 mg/g, vit D
 *  1 ng/mL = 2.496 nmol/L, ApoB 1 g/L = 100 mg/dL, CRP 1 mg/dL = 10 mg/L). Lp(a) mg/dL → nmol/L has no fixed factor: none. */
/** A factor, or a function for a conversion with an offset (HbA1c IFCC mmol/mol → NGSP %: % = 0.09148 × mmol/mol + 2.152). */
type Conversion = number | ((v: number) => number);
const CONVERSIONS: Partial<Record<string, Record<string, Conversion>>> = {
  hba1c: { 'mmol/mol': (v) => 0.09148 * v + 2.152 },
  ldl: { 'mg/dl': 1 / 38.67 },
  hdl: { 'mg/dl': 1 / 38.67 },
  totalchol: { 'mg/dl': 1 / 38.67 },
  nonhdl: { 'mg/dl': 1 / 38.67 },
  tg: { 'mg/dl': 1 / 88.57 },
  glucose_fasting: { 'mg/dl': 1 / 18.02 },
  apob: { 'mg/dl': 1 / 100 },
  uacr: { 'mg/g': 1 / 8.84 },
  vitd: { 'ng/ml': 2.496 },
  hscrp: { 'mg/dl': 10 },
};

const round = (v: number) => Math.round(v * 100) / 100;

/**
 * The reading in the marker's unit. Same unit (or none given): as is. A known conventional unit: converted, the reported
 * value kept in `reported`. Any other unit: `null` — the band cannot be applied to it.
 */
export function inMarkerUnit(marker: TerrainMarker, r: TerrainReading): TerrainReading | null {
  const from = unitKey(r.unit);
  if (from === unitKey(marker.unit)) return r;
  // A lab value with no unit is not assumed to be in the band's unit (it is shown as reported, not compared). Vitals come
  // from fixed vital codes whose unit is known, so a blank unit there is the marker's own.
  if (!from) return marker.kind === 'vital' ? r : null;
  const f = CONVERSIONS[marker.key]?.[from];
  if (f == null) return null;
  const apply = (v: number) => (typeof f === 'function' ? f(v) : v * f);
  const c = (v: number | null | undefined) => (v == null ? v : round(apply(v)));
  return { ...r, value: round(apply(r.value)), unit: marker.unit, refLow: c(r.refLow), refHigh: c(r.refHigh), reported: { value: r.value, unit: r.unit } };
}

export function buildRow(marker: TerrainMarker, readings: TerrainReading[] | undefined): MarkerRow {
  const sorted = [...(readings ?? [])].sort(byDate);
  const rawLatest = sorted.at(-1) ?? null;
  const rawPrevious = sorted.length > 1 ? sorted[sorted.length - 2] : null;
  const latestConverted = rawLatest ? inMarkerUnit(marker, rawLatest) : null;
  const previousConverted = rawPrevious ? inMarkerUnit(marker, rawPrevious) : null;
  // A unit the band cannot be read in: show the reading as reported, apply no band, and say why.
  const unitMismatch = !!rawLatest && !latestConverted;
  const latest = latestConverted ?? rawLatest;
  const previous = previousConverted ?? rawPrevious;
  const usable = unitMismatch ? [] : marker.bands.filter((b) => !b.needsRiskCategory);
  const band = usable[0] ?? null;
  const bandNote = band
    ? null
    : unitMismatch
      ? rawLatest!.unit.trim()
        ? `Reported in ${rawLatest!.unit}; the band is in ${marker.unit}, not compared`
        : `Reported with no unit; the band is in ${marker.unit}, not compared`
      : marker.bands.length
        ? RISK_CATEGORY_NOTE
        : NO_TARGET_NOTE;
  const counted = !!band && band.kind === 'target';
  if (!latest) {
    return { marker, latest, previous, band, bandNote, status: 'not-on-file', direction: null, labRange: null, counted, previousWithin: null };
  }
  const direction = band ? against(latest, band) : null;
  return {
    marker,
    latest,
    previous,
    band,
    bandNote,
    status: !band ? 'no-band' : direction === 'within' ? 'within' : 'outside',
    direction,
    labRange: labRange(latest),
    counted,
    previousWithin: band && previous && previousConverted ? against(previous, band) === 'within' : null,
  };
}

export function buildSystem(system: TerrainSystem, readings: TerrainReadings): SystemSummary {
  const rows = markersForSystem(system.id).map((m) => buildRow(m, readings[m.key]));
  const scored = rows.filter((r) => r.counted && r.latest);
  const inTarget = scored.filter((r) => r.status === 'within').length;
  const withPrevious = scored.filter((r) => r.previousWithin != null);
  // Judged = a reading compared against a band (a unit that could not be converted has no band and no direction).
  const judged = rows.filter((r) => r.latest && r.band && r.direction != null);
  const anyFlag = judged.some((r) => r.direction !== 'within');
  const judgedBefore = judged.filter((r) => r.previousWithin != null);
  return {
    system,
    rows,
    banded: scored.length,
    inTarget,
    previousBanded: withPrevious.length,
    previousInTarget: withPrevious.filter((r) => r.previousWithin).length,
    assessed: judged.length,
    assessedWithin: judged.filter((r) => r.direction === 'within').length,
    previousAssessed: judgedBefore.length,
    previousAssessedWithin: judgedBefore.filter((r) => r.previousWithin).length,
    state: judged.length === 0 ? 'not-derived' : anyFlag ? 'attention' : 'within',
  };
}

export function buildTerrain(readings: TerrainReadings): SystemSummary[] {
  return TERRAIN_SYSTEMS.map((s) => buildSystem(s, readings));
}

/** Problem-list entries per system, by the words in the condition's name (the demo's routing). Unmatched conditions are not shown. */
const CONDITION_WORDS: ReadonlyArray<[RegExp, TerrainSystemId]> = [
  // Cerebrovascular disease and stroke first: they would otherwise match the heart pattern's "vascular".
  [/cerebr|stroke/i, 'brain-nervous'],
  [/cardi|coronary|heart|vascular|atheroscl|hypertens|lipid|lipoprotein|cholesterol/i, 'heart-vessels'],
  [/diabet|glyc|insulin|metabolic|obes|weight/i, 'metabolism'],
  [/inflamm|immun|arthrit|lupus/i, 'inflammation-immunity'],
  [/liver|hepat|kidney|renal|anaem|anem/i, 'liver-kidney-blood'],
  [/thyroid|hormon|endocrin|testoster|menopaus|vitamin d/i, 'hormones'],
  [/sleep|apno|apnea|airway|snor|insomnia/i, 'sleep-recovery-airway'],
  [/brain|neuro|cognit|dementia|migraine/i, 'brain-nervous'],
];

/** ICD-10 code prefixes, checked before the words. `vitd` = where the catalog keeps vitamin D (E55 vitamin D deficiency). */
const CONDITION_CODES: ReadonlyArray<[RegExp, TerrainSystemId | 'vitd']> = [
  [/^E78/i, 'heart-vessels'], // disorders of lipoprotein metabolism, incl. E78.41 elevated Lp(a)
  [/^I(1[0-5]|2[0-5]|7[0-9])/i, 'heart-vessels'],
  [/^I6/i, 'brain-nervous'],
  [/^(E1[0-4]|R73|E66|E88\.81)/i, 'metabolism'],
  [/^E55/i, 'vitd'],
  [/^(K7[0-7]|N1[7-9]|D5|D6)/i, 'liver-kidney-blood'],
  [/^G47\.3/i, 'sleep-recovery-airway'],
];

/** The system the catalog lists first for vitamin D; Hormones if the catalog has no vitamin D marker. */
function vitaminDSystem(): TerrainSystemId {
  return TERRAIN_MARKERS.find((m) => m.key === 'vitd')?.systems[0] ?? 'hormones';
}

export function systemForCondition(name: string, code?: string | null): TerrainSystemId | null {
  const c = (code ?? '').trim();
  if (c) for (const [re, id] of CONDITION_CODES) if (re.test(c)) return id === 'vitd' ? vitaminDSystem() : id;
  for (const [re, id] of CONDITION_WORDS) if (re.test(name)) return id;
  return null;
}

/** "3 of 7 systems need attention". */
export function attentionCount(systems: SystemSummary[]): number {
  return systems.filter((s) => s.state === 'attention').length;
}

/** The band's limit in words, for the flag: "< 130/80 mmHg", "18.5–24.9 kg/m²", "≥ 90 mL/min/1.73m²". */
export function bandLimit(b: TerrainBand): string {
  if (b.diastolicHigh != null && b.high != null) return `< ${b.high}/${b.diastolicHigh} ${b.unit}`;
  if (b.low != null && b.high != null) return `${b.low}–${b.high} ${b.unit}`;
  if (b.high != null) return `${b.highInclusive ? '≤' : '<'} ${b.high} ${b.unit}`;
  if (b.low != null) return `≥ ${b.low} ${b.unit}`;
  return b.unit;
}
